import { gql } from '@apollo/client'
import { graphql } from '@/gql'
import { FORM_FIELD_ENTRY_FRAGMENT_EMPLOYEES_PAGE } from '@/graphql/fragments/FormField'
import { DEPARTMENT_TYPE_FRAGMENT_EMPLOYEES_PAGE } from '@/graphql/fragments/DepartmentType'
import { FUTURE_DATA_FRAGMENT_EMPLOYEES_PAGE } from '@/graphql/fragments/FutureData'

export const EMPLOYEES_SEARCH_SUGGESTIONS = graphql(`
  query employeesSearchSuggestions(
    $after: Cursor
    $searchQuery: String!
    $companyId: UUID!
    $and: [UserFilter!]
  ) {
    users: usersSearch(
      first: 10
      after: $after
      argSearchString: $searchQuery
      orderBy: [FIRST_NAME_ASC, LAST_NAME_ASC, ID_ASC]
      filter: {
        active: { equalTo: true }
        companyId: { equalTo: $companyId }
        and: $and
      }
    ) {
      edges {
        node {
          id
          employeeId
          logo
          fullName
        }
      }
      pageInfo {
        startCursor
        endCursor
        hasNextPage
      }
    }
  }
`)

export const EMPLOYEES_QUERY = graphql(`
  query allEployees(
    $first: Int
    $filter: EmployeeFilter
    $orderBy: [EmployeesOrderBy!]
    $after: Cursor
  ) {
    employees(
      first: $first
      orderBy: $orderBy
      after: $after
      filter: $filter
      condition: { active: true }
    ) {
      edges {
        node {
          id
          userId
          user {
            id
            logo
            locale
            fullName
            email
            emailWork
            userDepartments(
              filter: { department: { active: { equalTo: true } } }
            ) {
              edges {
                node {
                  departmentId
                  rankId
                  department {
                    id
                    name
                  }
                }
              }
            }
          }
        }
      }
      pageInfo {
        startCursor
        endCursor
        hasNextPage
      }
      totalCount
    }
  }
`)

export const USERS_PAGE_QUERY = (systemFields: string[]) => gql`
  query employeesPage(
    $first: Int
    $last: Int
    $after: Cursor
    $before: Cursor
    $orderBy: [UsersOrderBy!]
    $filter: UserFilter
    $companyId: UUID
    $systemFieldColumnNames: [String!]
    $formFieldColumnIds: [UUID!]
    $includeArchived: IncludeArchivedOption
    $includeOngoingProcesses: Boolean!
    $includeFinishedProcesses: Boolean!
    $includeOngoingPreboardings: Boolean!
    $includeFinishedPreboardings: Boolean!
    $includeDepartments: Boolean!
    $includeGroups: Boolean!
    $includeCompetences: Boolean!
  ) {
    users(
      first: $first
      last: $last
      after: $after
      before: $before
      orderBy: $orderBy
      filter: $filter
      condition: { active: true, companyId: $companyId }
      includeArchived: $includeArchived
    ) {
      edges {
        node {
          id
          isArchived
          isLeader
          departments
          leaderDepartments
          userSystemFields(filter: { systemField: { name: { in: $systemFieldColumnNames } } }) {
            nodes {
              data
              humanReadableValue
              systemFieldId
              systemField {
                id
                name
              }
            }
          }
          formFieldEntries(filter: { formFieldId: { in: $formFieldColumnIds } }
            condition: { active: true, objectType: "employee" }) {
            nodes {
              ...FormFieldEntryEmployeesPage
              formField {
                id
                sensitive
                supportsFutureData
                changesMustBeApproved
              }
            }
          }
          futureData(
            condition: { active: true, companyId: $companyId }
            orderBy: [UPDATE_VALUE_AT_ASC]
            filter: {
              or: [
                {
                  formFieldId: {
                    in: $formFieldColumnIds
                  }
                }
                {
                  systemField: {
                    name: {
                      in: $systemFieldColumnNames
                    }
                  }  
                }
              ]
            }
          ) {
            nodes {
              ...FutureDataEmployeesPage
            }
          }
          firstName
          lastName
          alternateFirstName
          alternateLastName
          ${systemFields.join('\n')}
          employeeByUserId {
            id
            ongoingProcesses: processes(first: 2, filter: {
              active: {
                equalTo: true
              }
              archived: {
                equalTo: false
              }
            }) @include(if: $includeOngoingProcesses) {
              totalCount
              nodes {
                id
                name
                progress
              }
            }
            finishedProcesses: processes(first: 2, filter: {
              active: {
                equalTo: true
              }
              archived: {
                equalTo: true
              }
            }) @include(if: $includeFinishedProcesses) {
              totalCount
              nodes {
                id
                name
                progress
              }
            }
            ongoingPreboardings: preboardingEmployees(first: 2, filter: {
              active: {
                equalTo: true
              }
              or: [
                {
                  completed: {
                    equalTo: false
                  }
                }
                {
                  published: {
                    equalTo: false
                  }
                }
              ]
              preboarding: {
                active: {
                  equalTo: true
                }
                anonymous: { equalTo: false }
              }
            }, orderBy: CREATED_AT_DESC) @include(if: $includeOngoingPreboardings) {
              totalCount
              nodes {
                id
                published
                completed
                preboarding {
                  id
                  name
                }
              }
            }
            finishedPreboardings: preboardingEmployees(first: 2, filter: {
              active: {
                equalTo: true
              }
              completed: {
                equalTo: true
              }
              published: {
                equalTo: true
              }
              preboarding: {
                active: {
                  equalTo: true
                }
                anonymous: { equalTo: false }
              }
            }, orderBy: CREATED_AT_DESC) @include(if: $includeFinishedPreboardings) {
              totalCount
              nodes {
                id
                published
                completed
                preboarding {
                  id
                  name
                }
              }
            }
          }
          userDepartments(
            filter: { department: { active: { equalTo: true } } }
          ) @include(if: $includeDepartments) {
            edges {
              node {
                departmentId
                rankId
                rank {
                  id
                  name
                }
                department {
                  id
                  name
                  departmentType {
                    ...DepartmentTypeEmployeesPage
                  }
                }
              }
            }
          }
          userGroups(filter: { group: { active: { equalTo: true } } }) @include(if: $includeGroups) {
            edges {
              node {
                group {
                  id
                  description
                  scope
                  name
                  localizedName
                }
              }
            }
          }
          userDepartmentGroups(
            filter: {
              group: { active: { equalTo: true } }
              department: { active: { equalTo: true } }
            }
          ) @include(if: $includeGroups) {
            edges {
              node {
                group {
                  id
                  description
                  scope
                  name
                  localizedName
                }
                department {
                  id
                  name
                }
              }
            }
          }
          userCompanyGroups(
            filter: { group: { active: { equalTo: true } } }
          ) @include(if: $includeGroups) {
            edges {
              node {
                group {
                  id
                  description
                  scope
                  name
                  localizedName
                }
              }
            }
          }
          userCompetences(
            condition: { active: true }
            filter: {
              competence: {
                active: { equalTo: true }
                competenceGroup: { active: { equalTo: true } }
              }
            }
          ) @include(if: $includeCompetences) {
            edges {
              node {
                id
                expireAt
                userCompetenceEvaluations(condition:{active: true}) {
                  nodes {
                    id
                    nextUpdate
                    evaluatingUserId
                    formFieldEntries {
                      nodes {
                        id
                        value
                      }
                    }
                  }
                }
                competence {
                  id
                  localizedName
                  outdateCompetence
                  ratingFormField {
                    metadata
                    fieldType
                    localizedHelpText
                  }
                  competenceGroup {
                    id
                    localizedName
                  }
                  reminderInterval {
                    days
                    hours
                    minutes
                    months
                    seconds
                    years
                  }
                }
              }
            }
          }
        }
      }
      pageInfo {
        startCursor
        endCursor
        hasNextPage
      }
    }
  }
  ${FORM_FIELD_ENTRY_FRAGMENT_EMPLOYEES_PAGE}
  ${DEPARTMENT_TYPE_FRAGMENT_EMPLOYEES_PAGE}
  ${FUTURE_DATA_FRAGMENT_EMPLOYEES_PAGE}
`

export const USERS_PAGE_TOTAL_COUNT_QUERY = gql`
  query usersPageTotalCount(
    $filter: UserFilter
    $companyId: UUID
    $includeArchived: IncludeArchivedOption
  ) {
    users(
      filter: $filter
      includeArchived: $includeArchived
      condition: { active: true, companyId: $companyId }
    ) {
      totalCount
    }
  }
`

export const USERS_SEARCH_TOTAL_COUNT_QUERY = gql`
  query usersSearchTotalCount(
    $filter: UserFilter
    $companyId: UUID
    $argSearchString: String
  ) {
    usersSearch(
      filter: $filter
      argSearchString: $argSearchString
      condition: { active: true, companyId: $companyId }
    ) {
      totalCount
    }
  }
`

/**
 * @deprecated Use USERS_PAGE_QUERY instead.
 * TODO: remove usage of this query
 */
export const EMPLOYEES_PAGE_QUERY = (systemFields: string[]) => gql`
  query employeesPageOld(
    $first: Int
    $last: Int
    $after: Cursor
    $before: Cursor
    $orderBy: [EmployeesOrderBy!]
    $filter: EmployeeFilter
    $includeArchived: IncludeArchivedOption
    $companyId: UUID
    $formFieldColumnIds: [UUID!]
  ) {
    employees(
      first: $first
      last: $last
      after: $after
      before: $before
      orderBy: $orderBy
      filter: $filter
      condition: { active: true, companyId: $companyId }
      includeArchived: $includeArchived
    ) {
      edges {
        node {
          id
          userId
          isArchived
          isLeader
          departments
          leaderDepartments
          formFieldEntries(
            filter: { formFieldId: { in: $formFieldColumnIds } }
            condition: { active: true, objectType: "employee" }
          ) {
            nodes {
              ...FormFieldEntryEmployeesPage
              formField {
                sensitive
              }
            }
          }
          user {
            ${systemFields.join('\n')}
            directManager {
              id
              fullName
            }
            userDepartments(
              filter: { department: { active: { equalTo: true } } }
            ) {
              edges {
                node {
                  departmentId
                  rankId
                  rank {
                    id
                    name
                  }
                  department {
                    id
                    name
                    departmentType {
                      ...DepartmentTypeEmployeesPage
                    }
                  }
                }
              }
            }
            userGroups(filter: { group: { active: { equalTo: true } } }) {
              edges {
                node {
                  group {
                    id
                    description
                    scope
                    name
                    localizedName
                  }
                }
              }
            }
            userDepartmentGroups(
              filter: {
                group: { active: { equalTo: true } }
                department: { active: { equalTo: true } }
              }
            ) {
              edges {
                node {
                  group {
                    id
                    description
                    scope
                    name
                    localizedName
                  }
                  department {
                    id
                    name
                  }
                }
              }
            }
            userCompanyGroups(
              filter: { group: { active: { equalTo: true } } }
            ) {
              edges {
                node {
                  group {
                    id
                    description
                    scope
                    name
                    localizedName
                  }
                }
              }
            }
            userCompetences(
              condition: { active: true }
              filter: {
                competence: {
                  active: { equalTo: true }
                  competenceGroup: { active: { equalTo: true } }
                }
              }
            ) {
              edges {
                node {
                  competence {
                    id
                    localizedName
                    competenceGroup {
                      id
                      localizedName
                    }
                  }
                }
              }
            }
          }
        }
      }
      pageInfo {
        startCursor
        endCursor
        hasNextPage
      }
      totalCount
    }

    systemFields {
      edges {
        node {
          id
          name
          companySystemField {
            id
            sensitive
            required
            fieldGroupId
            companySystemFieldOptions(
              orderBy: ORDER_ASC
              condition: { active: true }
            ) {
              nodes {
                id
                value: localizedValue
              }
            }
          }
        }
      }
    }
  }
  ${FORM_FIELD_ENTRY_FRAGMENT_EMPLOYEES_PAGE}
  ${DEPARTMENT_TYPE_FRAGMENT_EMPLOYEES_PAGE}
`

export const USERS_SEARCH_QUERY = (systemFields: string[]) => gql`
  query employeesSearch(
    $first: Int
    $last: Int
    $after: Cursor
    $before: Cursor
    $orderBy: [UsersOrderBy!]
    $filter: UserFilter
    $argSearchString: String
    $argFormFieldId: UUID
    $argOrderDirection: String
    $companyId: UUID
    $systemFieldColumnNames: [String!]
    $formFieldColumnIds: [UUID!]
    $includeOngoingProcesses: Boolean!
    $includeFinishedProcesses: Boolean!
    $includeOngoingPreboardings: Boolean!
    $includeFinishedPreboardings: Boolean!
    $includeDepartments: Boolean!
    $includeGroups: Boolean!
    $includeCompetences: Boolean!
    ) {
    usersSearch(
      first: $first
      last: $last
      after: $after
      before: $before
      orderBy: $orderBy
      filter: $filter
      argSearchString: $argSearchString
      argFormFieldId: $argFormFieldId
      argOrderDirection: $argOrderDirection
      condition: { active: true, companyId: $companyId }
    ) {
      edges {
        node {
          id
          isArchived
          isLeader
          departments
          userSystemFields(filter: { systemField: { name: { in: $systemFieldColumnNames } } }) {
            nodes {
              data
              humanReadableValue
              systemFieldId
              systemField {
                id
                name
              }
            }
          }
          formFieldEntries(filter: { formFieldId: { in: $formFieldColumnIds } }
            condition: { active: true, objectType: "employee" }) {
            nodes {
              ...FormFieldEntryEmployeesPage
              formField {
                id
                sensitive
                supportsFutureData
              }
            }
          }
          futureData(
            condition: { active: true, companyId: $companyId }
            orderBy: [UPDATE_VALUE_AT_ASC]
            filter: {
              or: [
                {
                  formFieldId: {
                    in: $formFieldColumnIds
                  }
                }
                {
                  systemField: {
                    name: {
                      in: $systemFieldColumnNames
                    }
                  }  
                }
              ]
            }
          ) {
            nodes {
              ...FutureDataEmployeesPage
            }
          }
          firstName
          lastName
          alternateFirstName
          alternateLastName
          ${systemFields.join('\n')}
          employeeByUserId {
            id
            ongoingProcesses: processes(first: 2, filter: {
              active: {
                equalTo: true
              }
              archived: {
                equalTo: false
              }
            }) @include(if: $includeOngoingProcesses) {
              totalCount
              nodes {
                id
                name
                progress
              }
            }
            finishedProcesses: processes(first: 2, filter: {
              active: {
                equalTo: true
              }
              archived: {
                equalTo: true
              }
            }) @include(if: $includeFinishedProcesses) {
              totalCount
              nodes {
                id
                name
                progress
              }
            }
            ongoingPreboardings: preboardingEmployees(first: 2, filter: {
              active: {
                equalTo: true
              }
              archived: {
                equalTo: false
              }
              or: [
                {
                  completed: {
                    equalTo: false
                  }
                }
                {
                  published: {
                    equalTo: false
                  }
                }
              ]
              preboarding: {
                active: {
                  equalTo: true
                }
              }
            }, orderBy: CREATED_AT_DESC) @include(if: $includeOngoingPreboardings) {
              totalCount
              nodes {
                id
                published
                completed
                preboarding {
                  id
                  name
                }
              }
            }
            finishedPreboardings: preboardingEmployees(first: 2, filter: {
              active: {
                equalTo: true
              }
              completed: {
                equalTo: true
              }
              published: {
                equalTo: true
              }
              preboarding: {
                active: {
                  equalTo: true
                }
              }
            }, orderBy: CREATED_AT_DESC) @include(if: $includeFinishedPreboardings) {
              totalCount
              nodes {
                id
                published
                completed
                preboarding {
                  id
                  name
                }
              }
            }
          }
          userDepartments(
            filter: { department: { active: { equalTo: true } } }
          ) @include(if: $includeDepartments) {
            edges {
              node {
                departmentId
                rankId
                rank {
                  id
                  name
                }
                department {
                  id
                  name
                  departmentType {
                    ...DepartmentTypeEmployeesPage
                  }
                }
              }
            }
          }
          userGroups(filter: { group: { active: { equalTo: true } } }) @include(if: $includeGroups) {
            edges {
              node {
                group {
                  id
                  description
                  scope
                  name
                  localizedName
                }
              }
            }
          }
          userDepartmentGroups(
            filter: {
              group: { active: { equalTo: true } }
              department: { active: { equalTo: true } }
            }
          ) @include(if: $includeGroups)  {
            edges {
              node {
                group {
                  id
                  description
                  scope
                  name
                  localizedName
                }
                department {
                  id
                  name
                }
              }
            }
          }
          userCompanyGroups(
            filter: { group: { active: { equalTo: true } } }
          ) @include(if: $includeGroups)  {
            edges {
              node {
                group {
                  id
                  description
                  scope
                  name
                  localizedName
                }
              }
            }
          }
          userCompetences(
            condition: { active: true }
            filter: {
              competence: {
                active: { equalTo: true }
                competenceGroup: { active: { equalTo: true } }
              }
            }
          ) @include(if: $includeCompetences) {
            edges {
              node {
                id
                expireAt
                userCompetenceEvaluations(condition:{active: true}) {
                  nodes {
                    id
                    nextUpdate
                    evaluatingUserId
                    formFieldEntries {
                      nodes {
                        id
                        value
                      }
                    }
                  }
                }
                competence {
                  id
                  localizedName
                  outdateCompetence
                  ratingFormField {
                    metadata
                    fieldType
                    localizedHelpText
                  }
                  competenceGroup {
                    id
                    localizedName
                  }
                  reminderInterval {
                    days
                    hours
                    minutes
                    months
                    seconds
                    years
                  }
                }
              }
            }
          }
        }
      }
      pageInfo {
        startCursor
        endCursor
        hasNextPage
      }
      totalCount
    }
  }
  ${FORM_FIELD_ENTRY_FRAGMENT_EMPLOYEES_PAGE}
  ${DEPARTMENT_TYPE_FRAGMENT_EMPLOYEES_PAGE}
  ${FUTURE_DATA_FRAGMENT_EMPLOYEES_PAGE}
`

export const DIRECT_MANAGER_BASIC_INFO = graphql(`
  query directManagerBasicInfo($userId: UUID!) {
    getDirectManagerBasicInfo(userId: $userId) {
      nodes {
        id
        fullName
        logo
      }
    }
  }
`)

export const USER_DIRECT_MANAGER_BASIC_INFO = graphql(`
  query userDirectManagerBasicInfo($userId: UUID!) {
    getUserDirectManagerBasicInfo(userId: $userId) {
      id
      employeeId
      fullName
      logo
    }
  }
`)

export const EMPLOYEES_BASIC_INFO = graphql(`
  query employeeBasicInfo($employeeIds: [UUID]!) {
    getEmployeesBasicInfo(employeeIds: $employeeIds) {
      nodes {
        id
        fullName
        logo
      }
    }
  }
`)

export const EMPLOYEES_BY_IDS_QUERY = graphql(`
  query employeesByIds(
    $ids: [UUID!]
    $first: Int
    $departmentsFilter: UUIDListFilter
    $orderBy: [EmployeesOrderBy!]
  ) {
    employees(
      filter: {
        active: { equalTo: true }
        id: { in: $ids }
        departments: $departmentsFilter
      }
      includeArchived: YES
      first: $first
      orderBy: $orderBy
    ) {
      nodes {
        id
        user {
          id
          logo
          fullName
          mobilePrivate
          mobile
          firstName
          lastName
          fullName
          email
          leaderDepartments
          departments
          userDepartments(
            filter: { department: { active: { equalTo: true } } }
          ) {
            edges {
              node {
                departmentId
              }
            }
          }
        }
      }
      totalCount
    }
  }
`)

export const EMPLOYEES_BY_USER_IDS_QUERY = graphql(`
  query employeeByUserId($ids: [UUID!], $first: Int) {
    employees(
      filter: { active: { equalTo: true }, userId: { in: $ids } }
      first: $first
      orderBy: [USER_BY_USER_ID__FIRST_NAME_ASC, USER_BY_USER_ID__LAST_NAME_ASC]
    ) {
      nodes {
        id
        user {
          id
          logo
          fullName
          mobilePrivate
          mobile
          firstName
          lastName
          email
          userDepartments(
            filter: { department: { active: { equalTo: true } } }
          ) {
            edges {
              node {
                departmentId
              }
            }
          }
        }
      }
      totalCount
    }
  }
`)

export const EMPLOYEES_FILTER = graphql(`
  query employeesFilter($companyId: UUID!) {
    employees(
      orderBy: [USER_BY_USER_ID__FIRST_NAME_ASC, USER_BY_USER_ID__LAST_NAME_ASC]
      filter: { active: { equalTo: true }, companyId: { equalTo: $companyId } }
    ) {
      edges {
        node {
          id
          user {
            id
            fullName
          }
        }
      }
    }
  }
`)

export const EMPLOYEES_DOWNLOAD_IMPORT_FILE = graphql(`
  query EmployeesDownloadImportFile(
    $delimiter: String!
    $onlyIncludeRequiredFields: Boolean
  ) {
    DownloadEmployeesFile(
      input: {
        delimiter: $delimiter
        onlyIncludeRequiredFields: $onlyIncludeRequiredFields
      }
    ) {
      file
    }
  }
`)

export const EMPLOYEES_ANALYSE_IMPORT_FILE = graphql(`
  query EmployeesAnalyseImportFile(
    $file: Upload!
    $mappings: [CsvMappingInput!]!
    $useDefaultCustomFieldValues: Boolean
    $delimiter: String
  ) {
    AnalyseImportEmployeesFromFile(
      input: {
        file: $file
        mappings: $mappings
        useDefaultCustomFieldValues: $useDefaultCustomFieldValues
        delimiter: $delimiter
      }
    ) {
      errors {
        value
        type
        name
        messageCode
        id
      }
    }
  }
`)

export const EMPLOYEE_DEPARTMENTS = graphql(`
  query employeeDepartments($ids: [UUID!]!) {
    employees(filter: { id: { in: $ids } }) {
      nodes {
        id
        departments
        user {
          directManager {
            employeeId
          }
        }
      }
    }
  }
`)

export const GET_EMPLOYEE_CSV_FIELD_MAPPING = graphql(`
  query getEmployeeCsvFieldMapping($csvFile: Upload!, $delimiter: String) {
    getEmployeeCsvFieldMapping(
      input: { csvFile: $csvFile, delimiter: $delimiter }
    ) {
      mappings {
        columnName
        fieldType
        fieldId
        fieldName
        options {
          fieldType
          fieldId
          fieldName
        }
      }
    }
  }
`)

export const ANALYSE_CSV_EMPLOYEE_UPDATE_FILE = graphql(`
  query analyseCsvEmployeeUpdateFile(
    $csvFile: Upload!
    $mappings: [CsvMappingInput!]!
    $clearFieldValuesWhenEmpty: Boolean
    $delimiter: String
  ) {
    analyseCsvEmployeeUpdateFile(
      input: {
        csvFile: $csvFile
        mappings: $mappings
        clearFieldValuesWhenEmpty: $clearFieldValuesWhenEmpty
        delimiter: $delimiter
      }
    ) {
      errors {
        id
        name
        value
        messageCode
        type
      }
    }
  }
`)

export const DOWNLOAD_EMPLOYEE_RECORD_UPDATE_TEMPLATE = graphql(`
  query downloadEmployeeRecordUpdateTemplate(
    $employeeRecordFieldId: UUID!
    $delimiter: String!
  ) {
    downloadEmployeeRecordUpdateTemplate(
      input: {
        employeeRecordFieldId: $employeeRecordFieldId
        delimiter: $delimiter
      }
    ) {
      base64
    }
  }
`)

export const ANALYSE_CSV_EMPLOYEE_RECORD_UPDATE_FILE = graphql(`
  query analyseCsvEmployeeRecordUpdateFile(
    $csvFile: Upload!
    $employeeRecordFieldId: UUID!
    $delimiter: String
  ) {
    analyseCsvEmployeeRecordUpdateFile(
      input: {
        csvFile: $csvFile
        employeeRecordFieldId: $employeeRecordFieldId
        delimiter: $delimiter
      }
    ) {
      errors {
        id
        name
        value
        messageCode
        type
      }
    }
  }
`)

export const EMPLOYEES_BY_IDS_WITH_DATE_FIELD_ENTRIES_QUERY = graphql(`
  query employeesByIdsWithDateFieldEntries(
    $ids: [UUID!]
    $first: Int
    $departmentsFilter: UUIDListFilter
    $orderBy: [EmployeesOrderBy!]
  ) {
    employees(
      filter: {
        active: { equalTo: true }
        id: { in: $ids }
        departments: $departmentsFilter
      }
      first: $first
      orderBy: $orderBy
    ) {
      nodes {
        id
        user {
          id
          logo
          fullName
          mobilePrivate
          mobile
          firstName
          lastName
          email
          employmentEnd
          employmentStart
          userDepartments(
            filter: { department: { active: { equalTo: true } } }
          ) {
            edges {
              node {
                departmentId
              }
            }
          }
        }
        formFieldEntries(
          condition: { active: true }
          filter: {
            and: {
              objectType: { equalTo: "employee" }
              formField: { fieldType: { equalTo: "date" } }
            }
          }
        ) {
          nodes {
            id
            value
            formField {
              id
              localizedName
              defaultValue
            }
          }
        }
      }
      totalCount
    }
  }
`)

export const ALL_USER_IDS_QUERY = graphql(`
  query allUserIds {
    users(
      filter: {
        and: [
          { active: { equalTo: true } }
          { isArchived: { equalTo: false } }
          { isFuture: { equalTo: false } }
          { isSystemUser: { equalTo: false } }
          { isResigned: { equalTo: false } }
        ]
      }
    ) {
      nodes {
        id
      }
    }
  }
`)

export const USERS_TOTAL_COUNT_BY_GROUP_IDS_AND_DEPARTMENT_IDS = graphql(`
  query usersTotalCountByGroupIdsAndDepartmentIds(
    $groupIds: [UUID!]
    $departmentIds: [UUID!]
    $employeeIds: [UUID!]
  ) {
    users(
      filter: {
        and: [
          {
            or: [
              { groupIds: { overlaps: $groupIds } }
              { departments: { overlaps: $departmentIds } }
              { id: { in: $employeeIds } }
            ]
          }
          {
            and: [
              { active: { equalTo: true } }
              { isArchived: { equalTo: false } }
              { isFuture: { equalTo: false } }
              { isSystemUser: { equalTo: false } }
              { isResigned: { equalTo: false } }
            ]
          }
        ]
      }
    ) {
      totalCount
    }
  }
`)
